import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useHistory, useParams, useLocation } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'
import UnauthorizedComponent from '../general/UnauthorizedComponent.jsx'
import * as XLSX from 'xlsx'

/* ─── design tokens (igual que CfdiComponent) ─── */
const T = {
    bg: '#f1f5f9', card: '#ffffff', border: '#e8edf5',
    blue: '#3b82f6', blueD: '#2563eb', navy: '#0f172a',
    txt: '#1e293b', txt2: '#64748b', muted: '#94a3b8',
    green: '#16a34a', greenBg: '#f0fdf4', greenBdr: '#bbf7d0',
    red: '#dc2626', redBg: '#fef2f2', redBdr: '#fecaca',
    warn: '#f59e0b', warnBg: '#fffbeb', warnBdr: '#fde68a',
    r: 16, rsm: 10,
    shadow: '0 1px 3px rgba(15,23,42,.06),0 1px 2px rgba(15,23,42,.04)',
    shadowMd: '0 4px 14px rgba(15,23,42,.09)',
    font: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif",
}

/* ─── helpers ─── */
const fmt = n => {
    if (n == null || isNaN(n)) return '-'
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n)
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

const STATUS_LIST = [
    { key: 'sociedadesActivasFlag',              label: 'Activas',               status: 'ACTIVA' },
    { key: 'sociedadesSinUtilizarFlag',          label: 'Sin Utilizar',          status: 'SIN UTILIZAR' },
    { key: 'sociedadesInactivasFlag',            label: 'Inactivas',             status: 'INACTIVA' },
    { key: 'sociedadesProcesoLiquidacionFlag',   label: 'Proceso Liquidación',   status: 'PROCESO LIQUIDACION' },
    { key: 'sociedadesLiquidadasFlag',           label: 'Liquidadas',            status: 'LIQUIDADA' },
    { key: 'sociedadesProcesoSuspensionFlag',    label: 'Proceso Suspensión',    status: 'PROCESO SUSPENSION' },
    { key: 'sociedadesSuspendidasFlag',          label: 'Suspendidas',           status: 'SUSPENDIDA' },
    { key: 'sociedadesVendidasFlag',             label: 'Vendidas',              status: 'VENDIDA' },
    { key: 'sociedadesDevueltasFlag',            label: 'Devueltas',             status: 'DEVUELTA' },
    { key: 'sociedadesNoRevisionFlag',           label: 'No Revisión',           status: 'NO REVISION' },
    { key: 'sociedadesSinStatusContableFlag',    label: 'Sin Estatus',           status: null },
]

/* ─── sub-components ─── */
function LoadingOverlay({ visible }) {
    if (!visible) return null
    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(241,245,249,.75)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
            <div style={{ background:T.card, borderRadius:T.r, padding:'32px 48px', boxShadow:T.shadowMd, display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
                <div className="zsp" />
                <span style={{ fontSize:14, color:T.txt2, fontWeight:500 }}>Cargando reporte...</span>
            </div>
        </div>
    )
}

function ErrorBanner({ message }) {
    if (!message) return null
    return (
        <div style={{ background:T.redBg, border:`1px solid ${T.redBdr}`, borderRadius:T.rsm, padding:'12px 16px', display:'flex', alignItems:'center', gap:8, marginBottom:20, fontSize:13.5, color:'#7f1d1d', fontWeight:500 }}>
            <span>⚠</span>{message}
        </div>
    )
}

function ActionButton({ label, icon, onClick, primary, disabled }) {
    const [hov, setHov] = useState(false)
    return (
        <button onClick={onClick} disabled={disabled}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{
                display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
                background: primary ? (hov ? T.blueD : T.blue) : (hov ? '#f8fafc' : T.card),
                border: `1px solid ${primary ? (hov ? T.blueD : T.blue) : T.border}`,
                borderRadius:T.rsm, color: primary ? '#fff' : T.txt,
                fontSize:13.5, fontWeight:550, fontFamily:T.font, cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1, transition:'all .15s', whiteSpace:'nowrap',
                boxShadow: primary && hov ? '0 4px 12px rgba(59,130,246,.35)' : T.shadow,
            }}>
            {icon && <span style={{ fontSize:16, lineHeight:1 }}>{icon}</span>}{label}
        </button>
    )
}

/* Autocomplete propio sin Material UI */
function MultiSelect({ label, options, value, onChange, getLabel, getId }) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const ref     = useRef(null)
    const inputRef = useRef(null)

    // Cierra al hacer click fuera
    useEffect(() => {
        const handler = e => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const filtered = options.filter(o =>
        !value.find(v => getId(v) === getId(o)) &&
        getLabel(o).toLowerCase().includes(query.toLowerCase())
    )

    const remove = id => onChange(value.filter(v => getId(v) !== id))

    // onMouseDown en opciones evita que el blur cierre el dropdown antes del click
    const add = opt => {
        onChange([...value, opt])
        setQuery('')
        inputRef.current?.focus()
    }

    return (
        <div ref={ref} style={{ position:'relative', width:'100%' }}>
            {label ? <label style={{ display:'block', fontSize:12, fontWeight:600, color:T.txt2, marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</label> : null}
            <div
                onClick={() => { setOpen(true); inputRef.current?.focus() }}
                style={{ minHeight:40, border:`1px solid ${open ? T.blue : T.border}`, borderRadius:T.rsm,
                    padding:'5px 10px', background:T.card, cursor:'text', display:'flex',
                    flexWrap:'wrap', gap:5, alignItems:'center', transition:'border .15s, box-shadow .15s',
                    boxShadow: open ? `0 0 0 3px ${T.blue}20` : 'none' }}>
                {value.map(v => (
                    <span key={getId(v)} style={{ display:'inline-flex', alignItems:'center', gap:4,
                        background:`${T.blue}12`, border:`1px solid ${T.blue}30`, borderRadius:6,
                        padding:'2px 8px', fontSize:12.5, color:T.blue, fontWeight:500, flexShrink:0 }}>
                        {getLabel(v)}
                        <span
                            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); remove(getId(v)) }}
                            style={{ cursor:'pointer', opacity:.7, lineHeight:1, fontSize:14 }}>×</span>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true) }}
                    onFocus={() => setOpen(true)}
                    placeholder={value.length === 0 ? 'Buscar...' : ''}
                    style={{ border:'none', outline:'none', fontSize:13.5, color:T.txt,
                        background:'transparent', flex:1, minWidth:60, fontFamily:T.font, padding:'2px 0' }} />
            </div>
            {open && (
                <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:300,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:T.rsm,
                    boxShadow:'0 8px 24px rgba(0,0,0,.12)', maxHeight:220, overflowY:'auto' }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding:'12px 14px', fontSize:13, color:T.muted, textAlign:'center' }}>
                            {query ? 'Sin resultados' : 'No hay más opciones'}
                        </div>
                    ) : filtered.slice(0, 60).map(opt => (
                        <div key={getId(opt)}
                            onMouseDown={e => { e.preventDefault(); add(opt) }}
                            style={{ padding:'9px 14px', fontSize:13.5, color:T.txt, cursor:'pointer',
                                borderBottom:`1px solid ${T.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background='#f0f7ff'}
                            onMouseLeave={e => e.currentTarget.style.background=''}>
                            {getLabel(opt)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

/* ═══════════════════ MAIN ═══════════════════ */
export default function ClienteReporteComponent(props) {
    const history   = useHistory()
    const location  = useLocation()
    const { id }    = useParams()

    const currentYear = new Date().getFullYear()

    const isAllMode = Number(id) === -1

    // Clientes pre-seleccionados desde la lista de proyectos (multi-select)
    const preSelectedClientes = location.state?.preSelectedClientes || []

    const [unauthorized,     setUnauthorized] = useState(false)
    const [loading,          setLoading]     = useState(!isAllMode)
    const [searching,        setSearching]   = useState(false)
    const [hasSearched,      setHasSearched] = useState(!isAllMode)
    const [page,             setPage]         = useState(1)
    const PAGE_SIZE = 50
    const [error,            setError]       = useState('')
    const [clientesDtoArray, setClientesDto] = useState([])
    const [allSociedades,    setAllSocs]     = useState([])
    const [allClientes,      setAllClis]     = useState([])
    const [inicioDate,       setInicio]      = useState(2018)
    const [finDate,          setFin]         = useState(currentYear)
    const [sociedades,       setSociedades]  = useState([])
    const [clientes,         setClientes]    = useState([])
    const [showMonths,       setShowMonths]  = useState(false)

    /* flags como objeto para evitar 10 estados separados */
    const [flags, setFlags] = useState(
        Object.fromEntries(STATUS_LIST.map(s => [s.key, true]))
    )
    const toggleFlag  = key => setFlags(f => ({ ...f, [key]: !f[key] }))
    const toggleAll   = val => setFlags(Object.fromEntries(STATUS_LIST.map(s => [s.key, val])))

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('READ_CLIENTES')) {
            setUnauthorized(true)
            setLoading(false)
            return
        }
        loadCatalogs()
    }, []) // eslint-disable-line

    // Sincronizar inicio/fin al rango real de datos cuando se cargan resultados
    useEffect(() => {
        const dataYears = new Set()
        clientesDtoArray.forEach(cd => {
            Object.values(cd.rfcContabilidad || {}).forEach(rfc => {
                Object.entries(rfc?.ingresosPerYear || {}).forEach(([y, v]) => { if (v) dataYears.add(Number(y)) })
                Object.entries(rfc?.nominaPerYear   || {}).forEach(([y, v]) => { if (v) dataYears.add(Number(y)) })
            })
        })
        if (dataYears.size === 0) return
        const sorted = [...dataYears].sort((a, b) => a - b)
        const minY = sorted[0]
        const maxY = sorted[sorted.length - 1]
        setInicio(prev => dataYears.has(prev) ? prev : minY)
        setFin(prev    => dataYears.has(prev) ? prev : maxY)
    }, [clientesDtoArray]) // eslint-disable-line

    // Función central de búsqueda — acepta lista explícita y rango de años
    const doSearch = useCallback(async (clientsList, inicio, fin) => {
        if (!clientsList.length) { setError('Selecciona al menos un proyecto para buscar.'); return }
        setError('')
        setSearching(true)
        setHasSearched(true)
        try {
            const params = {}
            if (inicio != null) params.añoInicio = inicio
            if (fin   != null) params.añoFin    = fin
            const results = await Promise.all(
                clientsList.map(c => ElementsDataService.retrieveElementParams('reporte-contable-cliente', c.id, params))
            )
            setClientesDto(results.flatMap(r => Array.isArray(r.data) ? r.data : [r.data]))
            setPage(1)
        } catch (err) {
            setError('Error al cargar el reporte. Por favor intenta de nuevo.')
        } finally {
            setSearching(false)
        }
    }, []) // eslint-disable-line

    const loadCatalogs = useCallback(async () => {
        try {
            if (isAllMode) {
                // allMode: solo cargar la lista de proyectos (ligera) para el dropdown.
                // NO cargar todas las sociedades aquí — pueden ser miles y causan OOM.
                // Las sociedades se derivan de los resultados de búsqueda.
                const cliR = await ElementsDataService.retrieveAllElements('clientes')
                setAllClis(cliR.data)
                if (preSelectedClientes.length > 0) {
                    setClientes(preSelectedClientes)
                    doSearch(preSelectedClientes, inicioDate, finDate)
                }
            } else {
                // Modo proyecto específico: sí necesitamos las sociedades para el filtro
                const [socR, cliR] = await Promise.all([
                    ElementsDataService.retrieveAllElements('sociedades'),
                    ElementsDataService.retrieveAllElements('clientes'),
                ])
                setAllSocs(socR.data)
                setAllClis(cliR.data)
                setLoading(true)
                const clienteR = await ElementsDataService.retrieveElement('clientes', id)
                const sociedadesLinked = clienteR.data.sociedades || []

                let rfcContabilidad = {}
                try {
                    const r = await ElementsDataService.retrieveElement('reporte-contable-cliente', id)
                    const dto = Array.isArray(r.data) ? r.data[0] : r.data
                    rfcContabilidad = dto?.rfcContabilidad || {}
                } catch (_) { /* sin datos contables, se muestran columnas vacías */ }

                setClientesDto([{
                    id: clienteR.data.id,
                    nombreCliente: clienteR.data.nombreCliente,
                    sociedades: sociedadesLinked,
                    rfcContabilidad,
                }])
                setHasSearched(true)
            }
        } catch (err) {
            setError('Error al cargar los datos. Por favor intenta de nuevo.')
        } finally {
            setLoading(false)
        }
    }, [id]) // eslint-disable-line

    // En allMode: si no hay proyectos seleccionados en el filtro, busca TODOS
    const handleBuscar = () => {
        const list = (isAllMode && clientes.length === 0) ? allClientes : clientes
        doSearch(list, inicioDate, finDate)
    }

    const handleBuscarEspecifico = async () => {
        setLoading(true)
        setError('')
        try {
            const clienteR = await ElementsDataService.retrieveElement('clientes', id)
            const sociedadesLinked = clienteR.data.sociedades || []
            let rfcContabilidad = {}
            try {
                const r = await ElementsDataService.retrieveElementParams('reporte-contable-cliente', id, { añoInicio: inicioDate, añoFin: finDate })
                const dto = Array.isArray(r.data) ? r.data[0] : r.data
                rfcContabilidad = dto?.rfcContabilidad || {}
            } catch (_) { /* sin datos contables — columnas muestran '-' */ }
            setClientesDto([{
                id: clienteR.data.id,
                nombreCliente: clienteR.data.nombreCliente,
                sociedades: sociedadesLinked,
                rfcContabilidad,
            }])
            setHasSearched(true)
        } catch (err) {
            setError('Error al cargar el reporte. Por favor intenta de nuevo.')
        } finally {
            setLoading(false)
        }
    }

    if (!AuthenticationService.isUserLoggedIn()) return null

    /* ── derivar nombre ── */
    const nombreCliente = clientesDtoArray.length === 1
        ? clientesDtoArray[0].nombreCliente
        : 'TODOS'

    /* ── años con datos reales (para los dropdowns) ── */
    const dataYearsSet = new Set()
    clientesDtoArray.forEach(cd => {
        Object.values(cd.rfcContabilidad || {}).forEach(rfc => {
            Object.entries(rfc?.ingresosPerYear || {}).forEach(([y, v]) => { if (v) dataYearsSet.add(Number(y)) })
            Object.entries(rfc?.nominaPerYear   || {}).forEach(([y, v]) => { if (v) dataYearsSet.add(Number(y)) })
        })
    })
    const yearOptions = dataYearsSet.size > 0
        ? [...dataYearsSet].sort((a, b) => a - b)
        : Array.from({ length: currentYear - 2015 + 2 }, (_, i) => 2015 + i)

    /* ── IDs seleccionados para filtrar ── */
    const sociedadesIds = new Set(sociedades.map(s => s.rfc))
    const clientesIds   = new Set(clientes.map(c => c.id))

    /* ── contadores de status ── */
    const counts = Object.fromEntries(STATUS_LIST.map(s => [s.key, 0]))

    /* ── construir filas y datos para Excel ── */
    let xlsxRows = []  // acumula filas de datos
    const MAX_YEARS = 15
    const años = []
    const _fin = Number(finDate)
    const _ini = Math.max(Number(inicioDate), _fin - MAX_YEARS + 1)
    for (let y = _fin; y >= _ini; y--) {
        if (dataYearsSet.size === 0 || dataYearsSet.has(y)) años.push(y)
    }

    /* acumuladores para el resumen global */
    let grandTotalIng = null
    let grandTotalNom = null
    let grandCount    = 0


    /* ── estilos de tabla ── */
    const thStyle = { padding:'10px 14px', fontSize:12, fontWeight:600, color:T.txt2, textAlign:'left', whiteSpace:'nowrap', background:'#f8fafc', borderBottom:`1px solid ${T.border}`, letterSpacing:'.03em', textTransform:'uppercase' }
    const thR     = { ...thStyle, textAlign:'right' }
    const tdBase  = { padding:'10px 14px', fontSize:13.5, color:T.txt, whiteSpace:'nowrap', verticalAlign:'top' }
    const tdWrap  = { ...tdBase, whiteSpace:'normal', wordBreak:'break-word', lineHeight:1.4 }   // para Razón Social
    const tdR     = { ...tdBase, textAlign:'right', fontVariantNumeric:'tabular-nums' }

    const rows = []

    clientesDtoArray.forEach(clienteDto => {
        if (clientesIds.size > 0 && !clientesIds.has(clienteDto.id)) return

        ;(clienteDto.sociedades || []).forEach(element => {
            if (sociedadesIds.size > 0 && !sociedadesIds.has(element.rfc)) return

            const rfc = clienteDto.rfcContabilidad?.[element.rfc]

            // Sumar solo los años del rango seleccionado (no el acumulado histórico)
            const totIng = años.reduce((acc, y) => {
                const v = rfc?.ingresosPerYear?.[y]
                return v != null ? (acc ?? 0) + v : acc
            }, null)
            const totNom = años.reduce((acc, y) => {
                const v = rfc?.nominaPerYear?.[y]
                return v != null ? (acc ?? 0) + v : acc
            }, null)

            // Omitir sociedades sin ningún dato en el periodo seleccionado (null o $0)
            const hasActivity = (totIng != null && totIng !== 0) || (totNom != null && totNom !== 0)
            if (!hasActivity) return

            /* contar por status — solo sociedades con actividad en el periodo */
            const match = STATUS_LIST.find(s =>
                s.status ? s.status === element.statusContabilidad : !element.statusContabilidad
            )
            if (match) { counts[match.key]++ }

            /* aplicar filtros de flags — si el status no está en la lista, mostrar siempre */
            const matchedEntry = match
            const visible = matchedEntry ? flags[matchedEntry.key] : true
            if (!visible) return

            /* celdas de años */
            const yearCells = []
            const xlsxRowData = {
                'Cliente': clienteDto.nombreCliente,
                'RFC': element.rfc,
                'Razón Social': element.razonSocial,
                'Tipo': element.tipo || '',
                'Estatus': element.statusContabilidad || '',
            }
            años.forEach(y => {
                const totalIng = rfc?.ingresosPerYear?.[y] ?? null
                yearCells.push(<td key={`ti-${y}`} style={tdR}>{fmt(totalIng)}</td>)
                xlsxRowData[`Total Ingresos ${y}`] = totalIng

                MESES.forEach((mes, mi) => {
                    const key = `${y}${mi + 1}`
                    const val = rfc?.ingresosPerYearMonth?.[key] ?? null
                    if (showMonths) yearCells.push(<td key={`im-${y}-${mi}`} style={tdR}>{fmt(val)}</td>)
                    xlsxRowData[`${mes} ${y}`] = val
                })

                const totalNom = rfc?.nominaPerYear?.[y] ?? null
                yearCells.push(<td key={`tn-${y}`} style={tdR}>{fmt(totalNom)}</td>)
                xlsxRowData[`Total Nómina ${y}`] = totalNom

                MESES.forEach((mes, mi) => {
                    const key = `${y}${mi + 1}`
                    const val = rfc?.nominaPerYearMonth?.[key] ?? null
                    if (showMonths) yearCells.push(<td key={`nm-${y}-${mi}`} style={tdR}>{fmt(val)}</td>)
                    xlsxRowData[`Nómina ${mes} ${y}`] = val
                })
            })

            /* acumular totales globales */
            if (totIng != null) grandTotalIng = (grandTotalIng ?? 0) + totIng
            if (totNom != null) grandTotalNom = (grandTotalNom ?? 0) + totNom
            grandCount++

            const rowKey = `${clienteDto.id}-${element.rfc}`
            rows.push(
                <tr key={rowKey} style={{ borderBottom:`1px solid ${T.border}` }}
                    onMouseEnter={e  => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e  => e.currentTarget.style.background=''}>
                    <td style={{ ...tdBase, maxWidth:100, overflow:'hidden', textOverflow:'ellipsis' }}>{clienteDto.nombreCliente}</td>
                    <td style={{ ...tdBase, maxWidth:140, fontFamily:'monospace', fontSize:12.5 }}>
                        {element.rfc && <Link to={`/sociedades/reportesContables/${element.rfc}`} style={{ color:T.blue, textDecoration:'none', fontWeight:500 }}>{element.rfc}</Link>}
                    </td>
                    <td style={{ ...tdWrap, maxWidth:260 }}>
                        <Link to={`/sociedades/reportes/${element.id}`} style={{ color:T.blue, textDecoration:'none' }}>{element.razonSocial}</Link>
                    </td>
                    <td style={{ ...tdBase, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis' }} title={element.tipo || ''}>{element.tipo || '-'}</td>
                    <td style={{ ...tdBase, minWidth:90 }}><StatusBadge status={element.statusContabilidad} /></td>
                    {yearCells}
                    <td style={{ ...tdR, fontWeight:600, color:T.green }}>{fmt(totIng)}</td>
                    <td style={{ ...tdR, fontWeight:600, color:T.blue  }}>{fmt(totNom)}</td>
                </tr>
            )

            xlsxRowData['Total Ingresos'] = totIng
            xlsxRowData['Total Nómina']   = totNom
            xlsxRows.push(xlsxRowData)
        })
    })

    const _applySheetStyles = (ws, data) => {
        if (!data.length) return
        const keys = Object.keys(data[0])
        ws['!cols'] = keys.map(k => {
            if (k === 'Razón Social') return { wch: 38 }
            if (k === 'RFC')          return { wch: 16 }
            if (k === 'Cliente')      return { wch: 14 }
            if (k === 'Tipo')         return { wch: 22 }
            if (k === 'Estatus')      return { wch: 16 }
            if (k.startsWith('TOTAL') || k.startsWith('Total')) return { wch: 18 }
            const maxLen = Math.max(k.length, ...data.map(r => String(r[k] ?? '').length))
            return { wch: Math.min(Math.max(maxLen + 2, 10), 18) }
        })
    }

    /* ── exportar Excel ── */
    const downloadExcel = () => {
        const wb   = XLSX.utils.book_new()
        const d    = new Date()
        const MESES_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']

        // ── Hoja Resumen ──────────────────────────────────────────────
        const resumenData = xlsxRows.map(r => {
            const row = {
                'Cliente':         r['Cliente'],
                'RFC':             r['RFC'],
                'Razón Social':    r['Razón Social'],
                'Tipo':            r['Tipo'],
                'Estatus':         r['Estatus'],
            }
            años.forEach(y => {
                row[`Ingresos ${y}`] = r[`Total Ingresos ${y}`] ?? null
                row[`Nómina ${y}`]   = r[`Total Nómina ${y}`]   ?? null
            })
            row['TOTAL INGRESOS'] = r['Total Ingresos']
            row['TOTAL NÓMINA']   = r['Total Nómina']
            return row
        })
        const wsRes = XLSX.utils.json_to_sheet(resumenData)
        _applySheetStyles(wsRes, resumenData)
        XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen')

        // ── Una hoja por año ──────────────────────────────────────────
        años.forEach(y => {
            const yearData = xlsxRows.map(r => {
                const row = {
                    'Cliente':      r['Cliente'],
                    'RFC':          r['RFC'],
                    'Razón Social': r['Razón Social'],
                    'Tipo':         r['Tipo'],
                    'Estatus':      r['Estatus'],
                    [`Total Ingresos`]: r[`Total Ingresos ${y}`] ?? null,
                }
                MESES_SHORT.forEach((ms, mi) => {
                    const mesNombre = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][mi]
                    row[`Ing ${ms}`] = r[`${mesNombre} ${y}`] ?? null
                })
                row[`Total Nómina`] = r[`Total Nómina ${y}`] ?? null
                MESES_SHORT.forEach((ms, mi) => {
                    const mesNombre = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][mi]
                    row[`Nom ${ms}`] = r[`Nómina ${mesNombre} ${y}`] ?? null
                })
                return row
            })
            const wsY = XLSX.utils.json_to_sheet(yearData)
            _applySheetStyles(wsY, yearData)
            XLSX.utils.book_append_sheet(wb, wsY, String(y))
        })

        XLSX.writeFile(wb, `Reporte_${nombreCliente}_${d.toISOString().slice(0,10)}.xlsx`)
    }

    const downloadCSV = downloadExcel  // alias para no cambiar el botón

    /* columnas de encabezado dinámicas */
    const dynHeaders = []
    años.forEach(y => {
        dynHeaders.push(<th key={`th-ti-${y}`} style={thR}>Total<br/>Ingresos<br/>{y}</th>)
        if (showMonths) MESES.forEach((m, mi) => dynHeaders.push(<th key={`th-im-${y}-${mi}`} style={thR}>Ingresos<br/>{m}<br/>{y}</th>))
        dynHeaders.push(<th key={`th-tn-${y}`} style={thR}>Total<br/>Nómina<br/>{y}</th>)
        if (showMonths) MESES.forEach((m, mi) => dynHeaders.push(<th key={`th-nm-${y}-${mi}`} style={thR}>Nómina<br/>{m}<br/>{y}</th>))
    })

    const allChecked  = STATUS_LIST.every(s => flags[s.key])


    const rawCounts = Object.fromEntries(STATUS_LIST.map(s => [s.key, 0]))
    let rawTotal = 0
    clientesDtoArray.forEach(cd => {
        if (clientesIds.size > 0 && !clientesIds.has(cd.id)) return
        cd.sociedades?.forEach(el => {
            if (sociedadesIds.size > 0 && !sociedadesIds.has(el.rfc)) return
            // Solo contar si tiene datos reales (> 0) en el periodo seleccionado
            const rfcData = cd.rfcContabilidad?.[el.rfc]
            const hasIng = años.some(y => rfcData?.ingresosPerYear?.[y] != null && rfcData.ingresosPerYear[y] !== 0)
            const hasNom = años.some(y => rfcData?.nominaPerYear?.[y]   != null && rfcData.nominaPerYear[y]   !== 0)
            if (!hasIng && !hasNom) return
            const m = STATUS_LIST.find(s => s.status ? s.status === el.statusContabilidad : !el.statusContabilidad)
            if (m) { rawCounts[m.key]++; rawTotal++ }
        })
    })
    const labelSt = { display:'block', fontSize:11, fontWeight:700, color:T.txt2, marginBottom:5, textTransform:'uppercase', letterSpacing:'.07em' }
    const inputSt = { width:100, padding:'8px 11px', border:`1px solid ${T.border}`, borderRadius:T.rsm, fontSize:14, color:T.txt, fontFamily:T.font, transition:'border .15s' }

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            {unauthorized && <UnauthorizedComponent history={history} />}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes zsp { to { transform:rotate(360deg); } }
                .zsp { width:28px; height:28px; border:3px solid ${T.border}; border-top-color:${T.blue}; border-radius:50%; animation:zsp .7s linear infinite; }
                .cr-input:focus { border-color: ${T.blue} !important; box-shadow: 0 0 0 3px ${T.blue}18 !important; outline:none; }
            `}</style>
            <LoadingOverlay visible={loading || searching} />
            <div style={{ maxWidth:1400, margin:'0 auto' }}>

                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <button onClick={() => history.goBack()}
                            style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:34, height:34,
                                borderRadius:9, border:`1px solid ${T.border}`, background:T.card, cursor:'pointer',
                                color:T.txt2, fontSize:16, boxShadow:T.shadow, flexShrink:0, fontFamily:T.font,
                                transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                            onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                            ‹
                        </button>
                        <div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>Reporte {nombreCliente}</h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>Ingresos y nómina timbrada por sociedad</p>
                        </div>
                    </div>
                    <ActionButton label="Descargar Excel" icon="📥" onClick={downloadCSV} primary disabled={rows.length === 0} />
                </div>

                <ErrorBanner message={error} />

                {/* Panel de control unificado */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, marginBottom:14 }}>

                    {/* Fila 1: Periodo + Filtros + Buscar */}
                    <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', gap:16, flexWrap:'wrap', alignItems:'flex-end' }}>
                        <div style={{ display:'flex', gap:8, alignItems:'flex-end', flexShrink:0 }}>
                            <div>
                                <label style={labelSt}>Año Inicio</label>
                                <select value={inicioDate}
                                    onChange={e => {
                                        const val = Number(e.target.value)
                                        setInicio(val)
                                        if (val > finDate) setFin(val)
                                    }}
                                    className="cr-input" style={{ ...inputSt, cursor:'pointer', paddingRight:28 }}>
                                    {yearOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                            <span style={{ color:T.muted, paddingBottom:9, fontSize:15 }}>→</span>
                            <div>
                                <label style={labelSt}>Año Fin</label>
                                <select value={finDate}
                                    onChange={e => {
                                        const val = Number(e.target.value)
                                        setFin(val)
                                        if (val < inicioDate) setInicio(val)
                                    }}
                                    className="cr-input" style={{ ...inputSt, cursor:'pointer', paddingRight:28 }}>
                                    {yearOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div style={{ width:1, height:38, background:T.border, flexShrink:0 }} />
                        {isAllMode && (
                            <div style={{ flex:'1 1 180px', minWidth:160 }}>
                                <label style={labelSt}>Proyectos</label>
                                <MultiSelect label="" options={allClientes} value={clientes}
                                    onChange={setClientes} getLabel={o => o.nombreCliente} getId={o => o.id} />
                            </div>
                        )}
                        <div style={{ flex:'1 1 180px', minWidth:160 }}>
                            <label style={labelSt}>Sociedades</label>
                            <MultiSelect label=""
                                options={isAllMode
                                    // allMode: sociedades derivadas de los resultados cargados (sin OOM)
                                    ? (() => {
                                        const seen = new Set()
                                        return clientesDtoArray.flatMap(cd => cd.sociedades || []).filter(s => {
                                            if (seen.has(s.rfc)) return false
                                            seen.add(s.rfc); return true
                                        })
                                    })()
                                    : (clientesDtoArray[0]?.sociedades || allSociedades)}
                                value={sociedades}
                                onChange={setSociedades}
                                getLabel={o => `${o.razonSocial} (${o.rfc})`}
                                getId={o => o.rfc} />
                        </div>
                        {isAllMode ? (
                            <ActionButton label={searching ? 'Buscando...' : 'Buscar'} icon={searching ? null : '🔍'}
                                onClick={handleBuscar} primary disabled={searching} />
                        ) : (
                            <ActionButton label={loading ? 'Cargando...' : 'Buscar'} icon={loading ? null : '🔍'}
                                onClick={handleBuscarEspecifico} primary disabled={loading} />
                        )}
                    </div>

                </div>

                {/* Filtrar por Estatus Contable — grilla de botones */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, marginBottom:14, overflow:'hidden' }}>
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ width:28, height:28, borderRadius:7, background:`${T.blue}12`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>📊</span>
                            <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Filtrar por Estatus Contable</h2>
                            {rawTotal > 0 && <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`, border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>{rawTotal} total</span>}
                        </div>
                        {/* Botón todos */}
                        <button onClick={() => toggleAll(!allChecked)}
                            style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'5px 14px', borderRadius:8, fontSize:12.5, fontWeight:600,
                                background: allChecked ? T.navy : '#f1f5f9',
                                border:`1px solid ${allChecked ? T.navy : T.border}`,
                                color: allChecked ? 'white' : T.txt2, cursor:'pointer', transition:'all .15s' }}>
                            {allChecked ? '✓ Todos' : '○ Todos'}
                        </button>
                    </div>
                    {/* Grilla de toggles */}
                    <div style={{ padding:'12px 20px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:6 }}>
                        {STATUS_LIST.map(s => {
                            const cnt = rawCounts[s.key]
                            const on  = flags[s.key]
                            const col = STATUS_COLORS[s.status] || { bg:'#f8fafc', border:'#e2e8f0', color:'#94a3b8' }
                            return (
                                <div key={s.key}
                                    onClick={() => toggleFlag(s.key)}
                                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                                        padding:'8px 12px', borderRadius:T.rsm, cursor:'pointer',
                                        background: on ? col.bg : '#fafbfd',
                                        border:`1px solid ${on ? col.border : T.border}`,
                                        transition:'all .15s',
                                        opacity: hasSearched && cnt === 0 ? .45 : 1 }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                                        <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
                                            background: on ? col.color : T.muted, transition:'background .15s' }} />
                                        <span style={{ fontSize:13, fontWeight:600, color: on ? col.color : T.txt2,
                                            transition:'color .15s', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                            {s.label}
                                        </span>
                                        {cnt > 0 && (
                                            <span style={{ fontSize:11.5, fontWeight:700, color: on ? col.color : T.muted,
                                                background: on ? `${col.color}18` : T.border+'88',
                                                borderRadius:10, padding:'1px 7px', flexShrink:0 }}>
                                                {cnt}
                                            </span>
                                        )}
                                    </div>
                                    {/* pointer-events:none so the toggle is visual only — click is handled by parent div */}
                                    <div style={{ pointerEvents:'none' }}>
                                        <ToggleSwitch checked={on} onChange={() => {}} />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* Tarjeta resumen de totales globales */}
                {hasSearched && grandCount > 0 && (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
                        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, padding:'16px 24px' }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Total Ingresos</div>
                            <div style={{ fontSize:22, fontWeight:800, color:T.green, letterSpacing:'-0.02em' }}>{fmt(grandTotalIng)}</div>
                            <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{grandCount} sociedad{grandCount !== 1 ? 'es' : ''} · {años.length === 1 ? años[0] : `${años[años.length-1]}–${años[0]}`}</div>
                        </div>
                        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, padding:'16px 24px' }}>
                            <div style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>Total Nómina</div>
                            <div style={{ fontSize:22, fontWeight:800, color:T.blue, letterSpacing:'-0.02em' }}>{fmt(grandTotalNom)}</div>
                            <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{grandCount} sociedad{grandCount !== 1 ? 'es' : ''} · {años.length === 1 ? años[0] : `${años[años.length-1]}–${años[0]}`}</div>
                        </div>
                    </div>
                )}

                {/* Tabla */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                    <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span>🏢</span>
                            <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>Sociedades</h2>
                            <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`, border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>{rows.length}</span>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                            {hasSearched && rows.length > 0 && (
                                <span style={{ fontSize:12, color:T.muted }}>{años.length} año{años.length !== 1 ? 's' : ''} · {años[años.length-1]}–{años[0]}</span>
                            )}
                            {hasSearched && rows.length > 0 && (
                                <button
                                    onClick={() => setShowMonths(v => !v)}
                                    style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:600,
                                        background: showMonths ? T.navy : T.card,
                                        border:`1px solid ${showMonths ? T.navy : T.border}`,
                                        color: showMonths ? 'white' : T.txt2,
                                        cursor:'pointer', transition:'all .15s', fontFamily:T.font }}>
                                    📅 {showMonths ? 'Ocultar meses' : 'Ver meses'}
                                </button>
                            )}
                            {/* Paginación */}
                            {rows.length > PAGE_SIZE && (() => {
                                const totalPages = Math.ceil(rows.length / PAGE_SIZE)
                                return (
                                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                        <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                                            style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`, background: page===1 ? T.bg : T.card,
                                                color: page===1 ? T.muted : T.txt, cursor: page===1 ? 'default':'pointer', fontSize:13, fontFamily:T.font }}>
                                            ‹
                                        </button>
                                        <span style={{ fontSize:12, color:T.txt2, whiteSpace:'nowrap' }}>
                                            {page} / {totalPages}
                                            <span style={{ color:T.muted }}> ({(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE, rows.length)} de {rows.length})</span>
                                        </span>
                                        <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                                            style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${T.border}`, background: page===totalPages ? T.bg : T.card,
                                                color: page===totalPages ? T.muted : T.txt, cursor: page===totalPages ? 'default':'pointer', fontSize:13, fontFamily:T.font }}>
                                            ›
                                        </button>
                                    </div>
                                )
                            })()}
                        </div>
                    </div>
                    {rows.length === 0 ? (
                        <div style={{ textAlign:'center', padding:'52px 24px' }}>
                            <div style={{ fontSize:36, marginBottom:10 }}>{hasSearched ? '📋' : '🔍'}</div>
                            <p style={{ margin:0, fontSize:15, fontWeight:700, color:T.txt }}>{hasSearched ? 'Sin datos contables' : 'Cargando proyectos...'}</p>
                            <p style={{ margin:'5px 0 0', fontSize:13, color:T.txt2 }}>{hasSearched ? (isAllMode ? 'Ajusta los filtros de estatus o proyectos' : 'Este proyecto no tiene sociedades vinculadas o no hay datos contables registrados') : 'Cargando todos los proyectos automáticamente'}</p>
                        </div>
                    ) : (
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ borderCollapse:'collapse', minWidth:'100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...thStyle, position:'sticky', left:0, zIndex:3, background:'#f8fafc', borderRight:`1px solid ${T.border}`, minWidth:80, maxWidth:100 }}>Proyecto</th>
                                        <th style={{ ...thStyle, minWidth:120, maxWidth:140 }}>RFC</th>
                                        <th style={{ ...thStyle, minWidth:200, maxWidth:260 }}>Razón Social</th>
                                        <th style={{ ...thStyle, minWidth:110, maxWidth:160 }}>Tipo</th>
                                        <th style={{ ...thStyle, minWidth:90 }}>Estatus</th>
                                        {dynHeaders}
                                        <th style={{ ...thR, borderLeft:`2px solid ${T.border}`, minWidth:110 }}>Total<br/>Ingresos</th>
                                        <th style={{ ...thR, minWidth:110 }}>Total<br/>Nómina</th>
                                    </tr>
                                </thead>
                                <tbody>{rows.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE)}</tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}


/* ─── StatusBadge ─── */
const STATUS_COLORS = {
    'ACTIVA':               { bg:'#f0fdf4', border:'#bbf7d0', color:'#16a34a' },
    'INACTIVA':             { bg:'#fef2f2', border:'#fecaca', color:'#dc2626' },
    'SIN UTILIZAR':         { bg:'#f8fafc', border:'#e2e8f0', color:'#64748b' },
    'LIQUIDADA':            { bg:'#fef2f2', border:'#fecaca', color:'#dc2626' },
    'PROCESO LIQUIDACION':  { bg:'#fffbeb', border:'#fde68a', color:'#d97706' },
    'SUSPENDIDA':           { bg:'#fef2f2', border:'#fecaca', color:'#dc2626' },
    'PROCESO SUSPENSION':   { bg:'#fffbeb', border:'#fde68a', color:'#d97706' },
    'VENDIDA':              { bg:'#eff6ff', border:'#bfdbfe', color:'#2563eb' },
    'DEVUELTA':             { bg:'#f5f3ff', border:'#ddd6fe', color:'#7c3aed' },
    'NO REVISION':          { bg:'#f8fafc', border:'#e2e8f0', color:'#64748b' },
}

function StatusBadge({ status, label }) {
    const s   = status || null
    const col = STATUS_COLORS[s] || { bg:'#f8fafc', border:'#e2e8f0', color:'#94a3b8' }
    const txt = label || s || 'Sin Estatus'
    return (
        <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, background:col.bg, border:`1px solid ${col.border}`, color:col.color, whiteSpace:'nowrap' }}>
            {txt}
        </span>
    )
}

/* ─── ToggleSwitch ─── */
function ToggleSwitch({ checked, indeterminate, onChange }) {
    return (
        <div onClick={() => onChange(!checked)} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:36, height:20, borderRadius:10, background: checked ? T.blue : (indeterminate ? T.warn : T.border), cursor:'pointer', transition:'background .2s', position:'relative', flexShrink:0 }}>
            <div style={{ width:14, height:14, borderRadius:7, background:'white', position:'absolute', left: checked ? 18 : 3, transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,.2)' }} />
        </div>
    )
}
